import { useEffect } from "react";
import { getEventIdFromHref } from "utility";
import {
  changeStyleWithHosting,
  getRedirectDomainByHostingType,
} from "utility/hosting";

import type { EventInfo } from "utility";

const DESCRIPTION_MAX_LENGTH = 120;

/**
 * 説明文は管理画面で HTML を手書きしており、段落などの要素の前後の改行は空白になる。
 * 改行を伴わないことが多い <br> だけ、前後の文がつながらないよう空白にする。
 */
const toDescriptionText = (html: string) => {
  const { body } = new DOMParser().parseFromString(html, "text/html");
  body.querySelectorAll("script, style").forEach((element) => {
    element.remove();
  });
  body.querySelectorAll("br").forEach((br) => {
    br.replaceWith(" ");
  });
  const text = (body.textContent ?? "").replace(/\s+/g, " ").trim();
  // 絵文字などを途中で切らないよう、文字（コードポイント）単位で数える
  const characters = Array.from(text);
  return characters.length > DESCRIPTION_MAX_LENGTH
    ? `${characters.slice(0, DESCRIPTION_MAX_LENGTH).join("")}…`
    : text;
};

/**
 * 表示中のイベントの title・meta description を head に入れ、パスが変わる・ページを離れるときに戻す。
 * 検索エンジンと SNS 向けの head は Hosting の関数（onEventPageV2）が HTML に入れる。ここはアプリ内で別のイベントや別のページへ移ったときのためのもの。
 * 関数が入れた title・canonical は開いたパスのものなので、イベントのデータが届いたかに関係なく、パスが変わる・離れるときに title をブランドの既定に戻し、canonical を外す（古いイベントの URL が共有されないように）。クエリやハッシュだけの変更では戻さない。
 * description が空のイベントは既定の description のままにする。
 */
export const useEventHeadMeta = ({
  event,
  pathname,
}: {
  event: Pick<
    EventInfo,
    "eventId" | "title" | "description" | "belongedHostings"
  > | null;
  pathname: string;
}) => {
  // イベントを切り替えた直後は前のイベントが store に残っているため、URL のイベントを表示しているときだけ入れる。所属ブランドのドメインへ飛ばすイベントは入れない
  const shownEvent =
    event &&
    event.eventId === getEventIdFromHref(pathname, "events") &&
    getRedirectDomainByHostingType(event.belongedHostings) === null
      ? event
      : null;
  const eventName = shownEvent?.title
    .replace(/<\/?br\s*\/?>/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  const brandTitle =
    changeStyleWithHosting().commonSettings.meta.title ?? "SPWN";
  // title が空のイベントは、関数と同じくブランドの title だけにする
  const title =
    eventName === undefined
      ? undefined
      : eventName === ""
        ? brandTitle
        : `${eventName} | ${brandTitle}`;
  const descriptionHtml = shownEvent?.description;

  useEffect(() => {
    if (title === undefined) {
      return;
    }
    const descriptionElement = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    );
    const previousDescription = descriptionElement?.content;
    const description = toDescriptionText(descriptionHtml ?? "");

    document.title = title;
    if (descriptionElement && description) {
      descriptionElement.content = description;
    }

    return () => {
      if (descriptionElement && previousDescription !== undefined) {
        descriptionElement.content = previousDescription;
      }
    };
  }, [title, descriptionHtml]);

  useEffect(
    () => () => {
      // 関数がイベントの title を入れて返したページでは変える前の値もイベントの title なので、ブランドの既定の title に戻す
      document.title =
        changeStyleWithHosting().commonSettings.meta.title ?? "SPWN";
      document.querySelector('link[rel="canonical"]')?.remove();
    },
    [pathname]
  );
};
